import React, { useState, useEffect, useRef } from 'react';
import { MapPin } from '@phosphor-icons/react';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

let scriptLoadingPromise = null;

function loadGoogleMapsScript() {
  if (window.google?.maps?.places) return Promise.resolve();
  if (scriptLoadingPromise) return scriptLoadingPromise;

  scriptLoadingPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
    script.async = true;
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });
  return scriptLoadingPromise;
}

/**
 * Address input backed by Google Places Autocomplete. The customer types
 * and picks a real suggestion from Google's own address database — this
 * is authoritative, accurate location data straight from Google, not a
 * guess from free text (which is what was causing the "very far off"
 * accuracy complaints with the free OpenStreetMap geocoding fallback).
 *
 * On selection, calls onPlaceSelected({ formattedAddress, lat, lng }).
 * If GOOGLE_MAPS_API_KEY isn't configured, quietly falls back to a plain
 * text input so the app never breaks — just loses the accuracy boost.
 */
export default function GooglePlacesAddressInput({ value, onChange, onPlaceSelected, placeholder }) {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!GOOGLE_MAPS_API_KEY) {
      setFailed(true);
      return;
    }
    let cancelled = false;
    loadGoogleMapsScript()
      .then(() => {
        if (cancelled || !inputRef.current) return;
        autocompleteRef.current = new window.google.maps.places.Autocomplete(inputRef.current, {
          fields: ['formatted_address', 'geometry', 'address_components'],
          componentRestrictions: { country: 'in' },
        });
        autocompleteRef.current.addListener('place_changed', () => {
          const place = autocompleteRef.current.getPlace();
          if (!place.geometry) return; // customer typed but didn't pick a suggestion
          onPlaceSelected({
            formattedAddress: place.formatted_address || '',
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng(),
          });
        });
        setReady(true);
      })
      .catch(() => setFailed(true));
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, []);

  return (
    <div className="relative">
      <input
        ref={inputRef}
        data-testid="google-places-address-input"
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder || 'Start typing your address...'}
        className="w-full pl-9 pr-3 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-100 focus:border-sspice transition-all"
      />
      <MapPin size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
      {failed && (
        <p className="text-[10px] text-gray-400 mt-1">
          (Address suggestions unavailable right now — you can still type it manually)
        </p>
      )}
    </div>
  );
}
